export const DEFAULT_REQUIREMENT_FORM = {
  display_name: '',
  min_count: 1,
  max_count: 1,
  onboarding_mode: 'manual',
  is_required_for_completion: true,
  display_order: 0
};

export function parse_response_data(payload) {
  if (typeof payload === 'string') {
    const trimmed_payload = payload.trim();

    if (!trimmed_payload) {
      const empty_payload = {};
      return empty_payload;
    }

    try {
      return JSON.parse(trimmed_payload);
    } catch {
      const empty_payload = {};
      return empty_payload;
    }
  }

  const fallback_payload = payload || {};
  return fallback_payload;
}

export function format_entity_date(entity) {
  const value = entity?.date_updated || entity?.date_created || '';
  if (!value) {
    return 'Recently updated';
  }

  return `Updated ${value}`;
}

function matches_entity_keyword(name, keywords) {
  return keywords.some((keyword) => name.includes(keyword));
}

export function get_entity_icon(entity, has_children = false) {
  const name = String(entity?.entity_type_name || '').toLowerCase();
  const building_keywords = ['trust', 'company', 'entity'];
  const document_keywords = ['document', 'file'];
  const people_keywords = ['beneficiar', 'trustee', 'founder', 'funder'];

  if (has_children || matches_entity_keyword(name, building_keywords)) {
    return 'bi-building';
  }

  if (matches_entity_keyword(name, document_keywords)) {
    return 'bi-file-earmark-text';
  }

  if (matches_entity_keyword(name, people_keywords)) {
    return 'bi-people';
  }

  return 'bi-diagram-3';
}

export function get_entity_tone(entity, has_children = false) {
  const name = String(entity?.entity_type_name || '').toLowerCase();

  if (name.includes('document') || name.includes('file')) {
    return 'highlight';
  }

  return has_children ? 'neutral' : 'neutral';
}

export function format_available_sub_entity(item, index) {
  const id = String(item?.id ?? '');
  const name = String(
    item?.entity_type_name ||
    item?.name ||
    `Entity Type ${index + 1}`
  ).trim();
  const formatted_sub_entity = { id, name };

  return formatted_sub_entity;
}

export function build_requirement_form(display_name = '', display_order = 0) {
  const requirement_form = {
    ...DEFAULT_REQUIREMENT_FORM,
    display_name: String(display_name || '').trim(),
    display_order: Number.isInteger(display_order) ? display_order : 0
  };

  return requirement_form;
}

export function normalize_max_count_input(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized_value = String(value).trim();
  if (!normalized_value) {
    return null;
  }

  const normalized_label = normalized_value.toLowerCase();
  if (
    normalized_label === 'no maximum' ||
    normalized_label === 'no max' ||
    normalized_label === 'unlimited' ||
    normalized_label === 'null'
  ) {
    return null;
  }

  return Number(normalized_value);
}

export function format_quantity_constraint(requirement) {
  const min_count = Number(requirement?.min_count || 0);
  const max_count = normalize_max_count_input(requirement?.max_count);

  if (max_count === null) {
    return `Minimum ${min_count}, No Maximum`;
  }

  return `Minimum ${min_count}, Maximum ${max_count}`;
}

export function build_requirement_summary(payload) {
  return format_quantity_constraint(payload);
}

export function format_onboarding_mode_label(onboarding_mode) {
  return onboarding_mode === 'self_service' ? 'Self Service' : 'Manual';
}

export function build_child_card(
  entity,
  assigned_child,
  assigned_children_by_parent_id,
  nested_child_id_set
) {
  const entity_id = Number(entity?.id || 0);
  const nested_children = assigned_children_by_parent_id.get(entity_id) || [];
  const expandable = nested_children.length > 0;
  const sub_entity_type_id = Number(
    assigned_child?.sub_entity_type_id ||
    assigned_child?.id ||
    assigned_child?.rule_id ||
    assigned_child?.sub_entity_binding_id ||
    0
  );
  const grandchildren = nested_children.map((grandchild) => {
    const child_entity_type_id = Number(grandchild.child_entity_type_id);
    const has_nested_children = nested_child_id_set.has(child_entity_type_id);

    return {
      id: `grandchild-${grandchild.child_entity_type_id}`,
      name: grandchild.child_entity_type_name,
      status_label: has_nested_children ? 'Has child entities' : 'Leaf child entity',
      status_tone: has_nested_children ? 'success' : 'danger',
      icon: 'bi-diagram-3'
    };
  });
  const child_card = {
    id: String(entity_id),
    sub_entity_type_id: Number.isInteger(sub_entity_type_id) && sub_entity_type_id > 0 ? String(sub_entity_type_id) : '',
    name: entity?.entity_type_name || `Entity ${entity_id}`,
    subtitle: entity?.entity_type_description || format_entity_date(entity),
    type_label: 'Child',
    child_summary: expandable
      ? `${nested_children.length} Direct Children`
      : 'No direct children',
    action_label: expandable ? 'Expand' : 'View Details',
    action_style: expandable ? 'link' : 'ghost',
    icon: get_entity_icon(entity, expandable),
    tone: get_entity_tone(entity, expandable),
    expandable,
    expanded: false,
    action_icon: 'bi-plus-circle',
    has_requirement: false,
    requirement_id: null,
    requirement_display_name: '',
    requirement_summary: '',
    grandchildren
  };

  return child_card;
}

export function build_hierarchy_cards(
  entities,
  assigned_children_by_parent_id,
  selected_parent_entity_type_id,
  collapsed_all
) {
  const entity_by_id = new Map(
    entities.map((entity) => [Number(entity.id), entity])
  );

  const all_child_ids = new Set();
  assigned_children_by_parent_id.forEach((children) => {
    children.forEach((child) => {
      all_child_ids.add(Number(child.child_entity_type_id));
    });
  });

  const parent_entities = entities.filter((entity) => {
    const assigned_children = assigned_children_by_parent_id.get(Number(entity.id)) || [];
    return assigned_children.length > 0;
  });

  const ordered_parent_entities = [...parent_entities].sort((left, right) => {
    const left_id = Number(left?.id || 0);
    const right_id = Number(right?.id || 0);

    if (selected_parent_entity_type_id) {
      if (left_id === selected_parent_entity_type_id) {
        return -1;
      }

      if (right_id === selected_parent_entity_type_id) {
        return 1;
      }
    }

    return String(left?.entity_type_name || '').localeCompare(String(right?.entity_type_name || ''));
  });

  return ordered_parent_entities.map((parent_entity) => {
    const entity_id = Number(parent_entity.id);
    const assigned_children = assigned_children_by_parent_id.get(entity_id) || [];
    const children = assigned_children
      .map((assigned_child) => {
        const child_entity = entity_by_id.get(Number(assigned_child.child_entity_type_id));
        if (!child_entity) {
          return null;
        }

        return build_child_card(
          child_entity,
          assigned_child,
          assigned_children_by_parent_id,
          all_child_ids
        );
      })
      .filter(Boolean);

    return {
      id: String(entity_id),
      name: parent_entity.entity_type_name || `Entity ${entity_id}`,
      subtitle: parent_entity.entity_type_description || format_entity_date(parent_entity),
      type_label: 'ROOT',
      child_summary: `${children.length} Child Entities`,
      icon: get_entity_icon(parent_entity, children.length > 0),
      tone: get_entity_tone(parent_entity, children.length > 0),
      expanded: !collapsed_all,
      accent_class: 'is-root-card',
      children
    };
  });
}

export function apply_requirement_to_hierarchy(hierarchy_cards, child_id, created_requirement, payload) {
  const requirement_summary = build_requirement_summary(payload);

  return hierarchy_cards.map((card) => ({
    ...card,
    children: (card.children || []).map((child) => (
      String(child.id) === String(child_id)
        ? {
            ...child,
            has_requirement: true,
            requirement_id: created_requirement?.id || child.requirement_id || null,
            requirement_display_name: created_requirement?.display_name || payload.display_name,
            requirement_summary
          }
        : child
    ))
  }));
}
